import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {useEyeconxStore} from '../../../../store/eyeconxStore';
import {toast} from 'react-toastify';
import settingsService from '../../../../services/settings.service';
import GatewayClinicInfoForm from './GatewayClinicInfoForm';
import GatewayClinicInfoText from './GatewayClinicInfoText';
import GatewayClinicInfoSkeleton from './GatewayClinicInfoSkeleton';
import ClinicLogoPanel from './ClinicLogoPanel';
import GatewayClinicHours from './GatewayClinicHours';
import SubHeader from '../../../layout/components/SubHeader';
import {ClinicStatusCard} from '../../../pages/settings/clinicsettings/info/clinicInfoParts';
import './GatewayClinicInfo.css';

function GatewayClinicInfo() {
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isHoursLoading, setIsHoursLoading] = useState(true);
    const [clinicInfo, setClinicInfo] = useState(null);
    const [clinicHours, setClinicHours] = useState([]);
    const clinicId = useEyeconxStore(state => state.user.clinicId);
    const isInitialLoad = isLoading && clinicInfo === null;

    useEffect(() => {
        getClinicInfo();
        getClinicHours();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clinicId]);

    const getClinicInfo = () => {
        setIsLoading(true);
        settingsService.getClinicById(clinicId)
            .then(res => {
                setClinicInfo(res.data);
            })
            .catch(err => {
                console.error("Error while getting Clinic Info", err);
                toast.error("Failed to load clinic information", {
                    position: "bottom-right"
                });
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const getClinicHours = () => {
        setIsHoursLoading(true);
        settingsService.getHours(clinicId)
            .then(res => {
                setClinicHours(res.data);
            })
            .catch(err => {
                console.error("Error while getting Clinic Hours", err);
            })
            .finally(() => {
                setIsHoursLoading(false);
            });
    };

    const updateClinicInfo = (data) => {
        setIsLoading(true);
        return settingsService.updateClinic(clinicId, data)
            .then(() => settingsService.getClinicById(clinicId))
            .then(res => {
                // PUT returns ClinicDTO without verification/timezone; refetch full clinic
                setClinicInfo(res.data);
                setIsEditing(false);
                return res;
            })
            .catch(err => {
                console.error("Error while updating Clinic", err);
                return Promise.reject(err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    return (
        <>
            <SubHeader
                title="Settings"
                description="Manage your clinic profile, contact details, and business hours"
                icon="bi-gear"
                iconClass="text-info"
                containerClass="container-fluid px-4"
            />
            <div className="container-fluid px-4 py-3">
            <div className="row g-3">
                <div className="col-12 col-lg-9">
                    <div className="card shadow-sm border-0 clinic-info-card gateway-settings-card">
                        <div className="card-header bg-white">
                            <div className="d-flex justify-content-between align-items-center w-100">
                                <div className="d-flex align-items-center">
                                    <i className="bi bi-building text-primary fs-5 me-2"></i>
                                    <div className="card-title mb-0">Clinic Information</div>
                                </div>
                                <div>
                                    {isEditing ? (
                                        <button
                                            className="btn btn-danger btn-sm text-white"
                                            onClick={() => setIsEditing(false)}
                                            disabled={isInitialLoad || isLoading}
                                        >
                                            <i className="bi bi-x-circle"></i> Cancel
                                        </button>
                                    ) : (
                                        <button
                                            className="btn btn-primary btn-sm text-white"
                                            onClick={() => setIsEditing(true)}
                                            disabled={isInitialLoad || isLoading}
                                        >
                                            <i className="bi bi-pencil-square me-1"></i> Edit
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="card-body">
                            {isInitialLoad ? (
                                <GatewayClinicInfoSkeleton/>
                            ) : (
                                isEditing ? (
                                    <GatewayClinicInfoForm
                                        clinicInfo={clinicInfo}
                                        updateInfo={updateClinicInfo}
                                    />
                                ) : (
                                    <GatewayClinicInfoText clinicInfo={clinicInfo}/>
                                )
                            )}
                        </div>
                    </div>
                </div>

                <div className="col-12 col-lg-3 d-flex flex-column gap-3 gateway-settings-sidebar">
                    {isInitialLoad ? (
                        <div className="card shadow-sm border-0 gateway-settings-card">
                            <div className="card-header bg-white">
                                <div className="mb-0 card-title">
                                    <i className="bi bi-image me-2"></i>
                                    Clinic Logo
                                </div>
                            </div>
                            <div className="card-body d-flex justify-content-center">
                                <div className="sms-skeleton clinic-info-logo-panel clinic-info-logo-panel--sidebar w-100">
                                    <span className="skeleton-block clinic-info-logo-skeleton"/>
                                </div>
                            </div>
                        </div>
                    ) : (
                        clinicInfo?.logo && (
                            <div className="card shadow-sm border-0 gateway-settings-card">
                                <div className="card-header bg-white">
                                    <div className="mb-0 card-title">
                                        <i className="bi bi-image me-2"></i>
                                        Clinic Logo
                                    </div>
                                </div>
                                <div className="card-body d-flex justify-content-center">
                                    <ClinicLogoPanel clinicInfo={clinicInfo}/>
                                </div>
                            </div>
                        )
                    )}

                    {isInitialLoad ? (
                        <div className="sms-skeleton clinic-info-status-card clinic-info-status-card--skeleton">
                            <span className="skeleton-block skeleton-circle flex-shrink-0" style={{width: 40, height: 40}}/>
                            <div className="flex-grow-1">
                                <span className="skeleton-block d-block mb-2" style={{height: 10, width: '45%'}}/>
                                <span className="skeleton-block d-block mb-2" style={{height: 16, width: '35%'}}/>
                                <span className="skeleton-block d-block" style={{height: 12, width: '80%'}}/>
                            </div>
                        </div>
                    ) : (
                        clinicInfo && <ClinicStatusCard isActive={clinicInfo.status === 'ON'} />
                    )}

                    <div className="card shadow-sm border-0 gateway-settings-card">
                        <div className="card-header bg-white">
                            <div className="mb-0 card-title">
                                <i className="bi bi-link-45deg me-2"></i>
                                Quick Links
                            </div>
                        </div>
                        <div className="card-body">
                            <div className="list-group list-group-flush">
                                <Link
                                    to="/sms-change-password"
                                    className="list-group-item list-group-item-action py-2 px-3"
                                >
                                    <i className="bi bi-key-fill text-secondary me-2"></i>
                                    Change Password
                                </Link>
                                <Link
                                    to="/sms-notifications"
                                    className="list-group-item list-group-item-action py-2 px-3"
                                >
                                    <i className="bi bi-bell-fill text-warning me-2"></i>
                                    Past Notifications
                                </Link>
                            </div>
                        </div>
                    </div>

                    <div className="card shadow-sm border-0 gateway-settings-card">
                        <div className="card-header bg-white">
                            <div className="mb-0 card-title">
                                <i className="bi bi-clock me-2"></i>
                                Clinic Hours
                            </div>
                        </div>
                        <div className="card-body">
                            <GatewayClinicHours hours={clinicHours} isLoading={isHoursLoading}/>
                        </div>
                    </div>
                </div>
            </div>
            </div>
        </>
    );
}

export default GatewayClinicInfo;